<template>
  <div>
    <a-card style="margin: 20px" title="订单管理">
      <!-- 筛选区域 -->
      <div class="selectBox">
        <div class="item">
          <span>订单号</span>
          <a-input v-model.trim="paramsData.orderNo" allow-clear placeholder="请输入订单号" style="width: 200px"/>
        </div>
        <div class="item">
          <span>商户协议编号</span>
          <a-input v-model.trim="paramsData.merchantNo" allow-clear placeholder="请输入商户协议编号" style="width: 180px"/>
        </div>
        <div class="item">
          <span>订单状态</span>
          <a-select v-model="paramsData.status" allow-clear placeholder="请选择状态" style="width: 150px">
            <a-select-option v-for="(item, key) in orderStatusDict" :key="key" :value="Number(key)">{{ item.label }}</a-select-option>
          </a-select>
        </div>
        <div class="item">
          <span>创建时间</span>
          <a-range-picker
              v-model="timeRange"
              :show-time="{format: 'HH:mm:ss'}"
              format="YYYY-MM-DD HH:mm:ss"
              style="width: 360px"
          />
        </div>
        <div class="item">
          <a-button type="primary" @click="handleQuery">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div class="tableBox">
        <div class="handleBtn">
          <a-button v-if="$authority('payAgrOrderAdd')" icon="plus" type="primary" @click="createOrder">创建订单</a-button>
          <a-button type="primary" @click="handleQuery">刷新</a-button>
        </div>
        <div class="tableBody">
          <a-table
              :columns="columns"
              :data-source="tableData"
              :pagination="false"
              :row-key="record => record.orderNo"
              :scroll="{x: 1400}"
          >
            <template slot="merchantInfo" slot-scope="text, record">
              <div>{{ record.merchantNo }}</div>
              <div style="font-size: 12px; color: #999;">{{ record.merchantName }}</div>
            </template>
            <template slot="payer" slot-scope="text, record">
              <WalletInfo :account-name="record.payerName" :account-no="text" :show-account-name="true"/>
            </template>
            <template slot="totalAmount" slot-scope="text">
              <Balance :amount="text" prefix="￥"/>
            </template>
            <template slot="payType" slot-scope="text">
              {{ getDictLabel(payAgrDict.payType, text) }}
            </template>
            <template slot="status" slot-scope="text, record">
              <a-popover v-if="record.failReason" title="失败原因">
                <template #content>{{ record.failReason }}</template>
                <a-tag :color="getDictColor(orderStatusDict, text)">{{ getDictLabel(orderStatusDict, text) }}</a-tag>
              </a-popover>
              <a-tag v-else :color="getDictColor(orderStatusDict, text)">{{ getDictLabel(orderStatusDict, text) }}</a-tag>
            </template>
            <template slot="action" slot-scope="text, record">
              <a-button-group size="small">
                <a-button v-if="$authority('payAgrOrderView')" icon="eye" style="color: #1890ff;" type="link" @click="viewBtn(record)">查看</a-button>
                <a-button
                    v-if="$authority('payAgrOrderExecute') && (record.status === 0 || record.status === 1)"
                    icon="thunderbolt" style="color: #722ed1;" type="link"
                    @click="triggerExecuteBtn(record)"
                >执行</a-button>
                <a-button
                    v-if="$authority('payAgrOrderRetry') && record.status === 4"
                    :loading="execLoadingNo === record.orderNo"
                    icon="sync" style="color: #ff7a45;" type="link"
                    @click="retryBtn(record)"
                >重试服务费</a-button>
              </a-button-group>
            </template>
          </a-table>
        </div>
      </div>

      <!-- 分页部分 -->
      <div class="pagination-wrapper">
        <LcfsPagination :fn="getTableData" :page-no.sync="pagination.pageNo" :page-size.sync="pagination.pageSize" :total="pagination.total"/>
      </div>
    </a-card>

    <!-- 短信验证码弹窗（列表页执行订单时使用） -->
    <a-modal
        v-model="smsModalVisible"
        title="短信验证并执行"
        :maskClosable="false"
        okText="确认执行"
        cancelText="取消"
        :confirmLoading="smsExecLoading"
        @ok="smsModalExecute"
        @cancel="smsModalCancel"
    >
      <p style="margin-bottom: 12px; color: #666;">
        订单 <strong>{{ smsOrder ? smsOrder.orderNo : '-' }}</strong>（{{ smsOrder ? smsOrder.totalAmount : '-' }} 元），
        验证码将发送至付款方预留手机。
      </p>
      <PaymentSms
          v-if="smsConfig && smsConfig.payerMobile"
          :verify-code.sync="verifyCode"
          :account-name="smsConfig.payerName"
          :account-no="smsConfig.payerAccount"
          :channel-no="smsConfig.channelNo"
          :app-no="smsConfig.appNo"
          :pay-amount="smsOrder ? smsOrder.totalAmount : 0"
          model-no="102216"
          :mobile="smsConfig.payerMobile"
          style="width: 300px;"
      />
      <a-alert
          v-else
          type="warning"
          show-icon
          message="付款方账户未登记手机号，无法发送验证码，请联系管理员补充。"
      />
    </a-modal>
  </div>
</template>

<script>
import {message} from "ant-design-vue";
import LcfsPagination from "@/view/components/LcfsPagination.vue";
import Balance from "@/view/components/Balance.vue";
import WalletInfo from "@/view/components/WalletInfo.vue";
import PaymentSms from "@/view/components/PaymentSms.vue";
import {payAgrDict, getDictLabel, getDictColor} from "@/utils/bizDict/payAgrDict";

const orderStatusDict = {...payAgrDict.orderStatus};
delete orderStatusDict.default;

export default {
  name: 'PayAgrOrder',
  components: {LcfsPagination, Balance, WalletInfo, PaymentSms},
  data() {
    return {
      payAgrDict,
      orderStatusDict,
      pagination: {pageNo: 1, pageSize: 10, total: 0},
      tableData: [],
      timeRange: [],
      paramsData: {
        orderNo: '',
        merchantNo: '',
        status: undefined,
      },
      execLoadingNo: null,
      firstActivated: true,
      // SMS 验证码弹窗
      smsModalVisible: false,
      smsOrder: null,   // 当前执行的订单记录
      smsConfig: null,  // 对应商户配置（含付款方 channelNo/appNo/payerMobile）
      verifyCode: '',
      smsExecLoading: false,
      columns: [
        {
          title: "No",
          customRender: (text, record, index) => `${(this.pagination.pageNo - 1) * this.pagination.pageSize + (index + 1)}`,
          align: "center",
          width: 50
        },
        {title: "订单号", dataIndex: "orderNo", align: "center", width: 180},
        {title: "商户协议", dataIndex: "merchantNo", align: "center", width: 160, scopedSlots: {customRender: 'merchantInfo'}},
        {title: "付款方", dataIndex: "payerAccount", align: "center", width: 160, scopedSlots: {customRender: 'payer'}},
        {title: "订单总金额", dataIndex: "totalAmount", align: "center", width: 120, scopedSlots: {customRender: 'totalAmount'}},
        {title: "支付类型", dataIndex: "payType", align: "center", width: 100, scopedSlots: {customRender: 'payType'}},
        {title: "状态", dataIndex: "status", align: "center", width: 120, scopedSlots: {customRender: 'status'}},
        {title: "支付时间", dataIndex: "payTime", align: "center", width: 150, customRender: (text) => this.formatDateTime(text)},
        {title: "创建时间", dataIndex: "createTime", align: "center", width: 150, customRender: (text) => this.formatDateTime(text)},
        {title: "操作", key: "action", width: 300, align: "center", fixed: 'right', scopedSlots: {customRender: 'action'}},
      ],
    };
  },
  mounted() {
    // 支持从统计页跳转携带商户协议编号
    if (this.$route.query.merchantNo) {
      this.paramsData.merchantNo = this.$route.query.merchantNo;
    }
    this.getTableData();
  },
  activated() {
    // 列表页被 keep-alive 缓存后，从创建/详情页返回时 mounted 不再触发，这里补刷新
    if (this.firstActivated) {
      this.firstActivated = false;
      return;
    }
    if (this.$route.query.merchantNo) {
      this.paramsData.merchantNo = this.$route.query.merchantNo;
    }
    this.getTableData();
  },
  methods: {
    getDictLabel,
    getDictColor,

    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      try {
        const date = new Date(dateTime);
        return date.toLocaleString('zh-CN');
      } catch (e) {
        return dateTime;
      }
    },

    buildTimeParams() {
      // a-range-picker 绑定的是 moment 对象，优先用 moment.format
      const fmt = (m) => (m && typeof m.format === 'function') ? m.format('YYYY-MM-DD HH:mm:ss') : '';
      if (this.timeRange && this.timeRange.length === 2) {
        return {createTimeStart: fmt(this.timeRange[0]), createTimeEnd: fmt(this.timeRange[1])};
      }
      return {createTimeStart: '', createTimeEnd: ''};
    },

    async getTableData() {
      try {
        const res = await this.api.getPayAgrOrderList({
          ...this.paramsData,
          ...this.buildTimeParams(),
          pageNo: this.pagination.pageNo,
          pageSize: this.pagination.pageSize,
        });
        if (res.code === 200) {
          this.tableData = res.data.records || [];
          this.pagination.total = res.data.total || 0;
        } else {
          message.warning(res.info || "获取数据失败");
        }
      } catch (error) {
        console.error("获取数据失败:", error);
        message.error("获取数据失败");
      }
    },

    handleQuery() {
      this.pagination.pageNo = 1;
      this.getTableData();
    },

    handleReset() {
      this.paramsData = {orderNo: '', merchantNo: '', status: undefined};
      this.timeRange = [];
      this.pagination.pageNo = 1;
      this.getTableData();
    },

    createOrder() {
      this.$router.push({path: '/pay-agr/order/create'});
    },

    viewBtn(record) {
      this.$router.push({path: '/pay-agr/order/detail', query: {orderNo: record.orderNo}});
    },

    async triggerExecuteBtn(record) {
      // configSnapshot 含付款方 channelNo/appNo；手机号实时从 CustAccountInfo 查询（不做冗余）
      let snapshot = {};
      try {
        snapshot = record.configSnapshot ? JSON.parse(record.configSnapshot) : {};
      } catch (e) {
        console.warn('configSnapshot 解析失败:', e);
      }
      const channelNo = snapshot.channelNo || record.channelNo || '';
      let payerMobile = '';
      try {
        const res = await this.api.accountqueryBalancedApi({
          channel_no: channelNo,
          account_no: record.payerAccount,
        });
        if (res.code === 200 && res.data) {
          payerMobile = res.data.mobile || '';
        }
      } catch (e) {
        console.error('查询付款方手机号失败:', e);
      }
      this.smsOrder = record;
      this.smsConfig = {
        payerAccount: record.payerAccount,
        payerName: record.payerName,
        channelNo: channelNo,
        appNo: snapshot.appNo || record.appNo || '',
        payerMobile: payerMobile,
      };
      this.verifyCode = '';
      this.smsModalVisible = true;
    },

    async smsModalExecute() {
      if (!this.verifyCode || this.verifyCode.length < 4) {
        message.warning('请输入短信验证码');
        return;
      }
      this.smsExecLoading = true;
      try {
        const res = await this.api.triggerPayAgrExecute({
          orderNo: this.smsOrder.orderNo,
          verifyCode: this.verifyCode,
        });
        if (res.code === 200 && res.data) {
          this.smsModalVisible = false;
          this.showExecuteResult(res.data);
        } else {
          message.warning(res.info || '执行失败');
        }
        this.getTableData();
      } catch (error) {
        console.error('执行失败:', error);
        message.error('执行失败');
      } finally {
        this.smsExecLoading = false;
      }
    },

    smsModalCancel() {
      this.smsModalVisible = false;
    },

    retryBtn(record) {
      this.$confirm({
        title: '重试服务费',
        content: `确认重新收取订单【${record.orderNo}】中失败的服务费？不会重发订单付款。`,
        onOk: async () => {
          this.execLoadingNo = record.orderNo;
          try {
            const res = await this.api.retryPayAgrServiceFee({orderNo: record.orderNo});
            if (res.code === 200 && res.data) {
              this.showExecuteResult(res.data);
            } else {
              message.warning(res.info || '重试失败');
            }
            this.getTableData();
          } catch (error) {
            console.error("重试失败:", error);
            message.error('重试失败');
          } finally {
            this.execLoadingNo = null;
          }
        },
      });
    },

    showExecuteResult(data) {
      const status = data.status;
      if (status === 3) {
        message.success(`订单 ${data.orderNo} 执行完成`);
      } else if (status === 4) {
        message.warning(`订单 ${data.orderNo} 存在服务费失败，可在重试管理中重试`);
      } else if (status === 5) {
        message.error(`订单 ${data.orderNo} 付款失败，订单已终止`);
      } else if (status === 2) {
        message.warning(`订单 ${data.orderNo} 存在处理中的记录，系统不会自动重发，请稍后核实结果`);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.selectBox {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 16px;

  .item {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    margin-right: 20px;

    span {
      margin-right: 8px;
      white-space: nowrap;
    }
  }
}

.tableBox {
  margin-bottom: 20px;

  .handleBtn {
    margin-bottom: 16px;

    .ant-btn {
      margin-right: 8px;
    }
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding: 12px 0;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 6px 6px;
}
</style>
